/* eslint-disable max-lines -- not split yet: grid/tier helpers and StatCard/SmsTypeStatCard are separable */
import type {
  ShortUrlClickLatencyStatsRecord,
  SmsTypeClickLatencyRecord,
  WeekdayClickLatencyRecord
} from '@navio/nsdk';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiTooltip } from 'noddi-ui-web';
import { BOOKING_SMS_TYPE_OPTIONS, secondsToTimeString } from 'noddi-util';
import { Fragment, useMemo } from 'react';

const HOURS = Array.from({ length: 15 }, (_, i) => i + 8); // 8..22

const smsTypeLabelMap = new Map<string, string>(BOOKING_SMS_TYPE_OPTIONS.map((o) => [o.value, o.label]));

const getSmsTypeLabel = (value: string) =>
  smsTypeLabelMap.get(value) ?? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatHour = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

const formatLatency = (seconds: number) =>
  secondsToTimeString({ seconds: Math.round(seconds), shortFormat: true, alwaysIncludeSeconds: false });

const TRAFFIC_TIERS = [
  { bg: 'bg-emerald-500', border: 'border-emerald-500/40' },
  { bg: 'bg-emerald-400', border: 'border-emerald-400/40' },
  { bg: 'bg-amber-300', border: 'border-amber-300/40' },
  { bg: 'bg-orange-400', border: 'border-orange-400/40' },
  { bg: 'bg-red-400', border: 'border-red-400/40' }
] as const;

const getTrafficTier = (avgSeconds: number, minSeconds: number, maxSeconds: number) => {
  const range = maxSeconds - minSeconds;
  if (range === 0) {
    return { bg: 'bg-primary/50', border: 'border-primary/30' };
  }
  const ratio = (avgSeconds - minSeconds) / range;
  const index = Math.min(Math.floor(ratio * 5), 4);
  return TRAFFIC_TIERS[index]!;
};

const WowTrend = ({ pct }: { pct: number | null }) => {
  if (pct === null) {
    return null;
  }
  // Positive = slower (bad), negative = faster (good)
  const isFaster = pct < 0;
  const absPct = Math.abs(pct);
  const formatted = absPct < 10 ? absPct.toFixed(1) : Math.round(absPct).toString();

  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs', isFaster ? 'text-emerald-600' : 'text-red-500')}>
      <NoddiIcon name={isFaster ? 'TrendingDown' : 'TrendingUp'} size='xSmall' />
      {formatted}%
    </span>
  );
};

type HeatmapCell = {
  weekday: number;
  weekdayName: string;
  hour: number;
  avgSeconds: number | null;
  clickCount: number;
  wowTrendPct: number | null;
};

const buildGridMap = (byWeekday: WeekdayClickLatencyRecord[]): Map<string, HeatmapCell> => {
  const map = new Map<string, HeatmapCell>();

  for (const wd of byWeekday) {
    const hourMap = new Map(wd.byHour.map((h) => [h.hour, h]));
    for (const hour of HOURS) {
      const hourData = hourMap.get(hour);
      map.set(`${wd.weekday}-${hour}`, {
        weekday: wd.weekday,
        weekdayName: wd.weekdayName,
        hour,
        avgSeconds: hourData?.avgSeconds ?? null,
        clickCount: hourData?.clickCount ?? 0,
        wowTrendPct: hourData?.wowTrendPct ?? null
      });
    }
  }

  return map;
};

const getCellColor = (avgSeconds: number | null, minSeconds: number, maxSeconds: number, isBest: boolean): string => {
  if (avgSeconds === null) {
    return 'bg-muted/30';
  }
  if (isBest) {
    return 'bg-sky-500 ring-2 ring-sky-300';
  }
  return getTrafficTier(avgSeconds, minSeconds, maxSeconds).bg;
};

const groupByLatencyTier = (
  items: SmsTypeClickLatencyRecord[]
): { label: string; items: SmsTypeClickLatencyRecord[] }[] => {
  const tiers = [
    { label: 'Fast (< 5 min)', items: [] as SmsTypeClickLatencyRecord[] },
    { label: 'Medium (5-30 min)', items: [] as SmsTypeClickLatencyRecord[] },
    { label: 'Slow (> 30 min)', items: [] as SmsTypeClickLatencyRecord[] }
  ];

  for (const item of items) {
    if (item.avgSeconds < 300) {
      tiers[0]!.items.push(item);
    } else if (item.avgSeconds < 1800) {
      tiers[1]!.items.push(item);
    } else {
      tiers[2]!.items.push(item);
    }
  }

  return tiers.filter((t) => t.items.length > 0);
};

type Props = {
  data: ShortUrlClickLatencyStatsRecord;
};

export const ClickLatencyHeatmap = ({ data }: Props) => {
  const gridMap = useMemo(() => buildGridMap(data.byWeekday), [data.byWeekday]);

  const { minSeconds, maxSeconds, bestWeekday, bestHour } = useMemo(() => {
    const withData = [...gridMap.values()].filter((c) => c.avgSeconds !== null);
    if (withData.length === 0) {
      return { minSeconds: 0, maxSeconds: 0, bestWeekday: -1, bestHour: -1 };
    }
    const values = withData.map((c) => c.avgSeconds!);
    const min = Math.min(...values);
    const best = withData.find((c) => c.avgSeconds === min)!;
    return {
      minSeconds: min,
      maxSeconds: Math.max(...values),
      bestWeekday: best.weekday,
      bestHour: best.hour
    };
  }, [gridMap]);

  const sortedWeekdays = useMemo(
    () =>
      data.byWeekday
        .slice()
        .sort((a, b) => a.weekday - b.weekday)
        .map((wd) => ({ weekday: wd.weekday, name: wd.weekdayName })),
    [data.byWeekday]
  );

  const weekdayRange = useMemo(() => {
    const values = data.byWeekday.map((wd) => wd.avgSeconds);
    if (values.length === 0) {
      return { min: 0, max: 0 };
    }
    return { min: Math.min(...values), max: Math.max(...values) };
  }, [data.byWeekday]);

  const smsTypeRange = useMemo(() => {
    if (data.bySmsType.length === 0) {
      return { min: 0, max: 0 };
    }
    const values = data.bySmsType.map((s) => s.avgSeconds);
    return { min: Math.min(...values), max: Math.max(...values) };
  }, [data.bySmsType]);

  const smsTypeTiers = useMemo(
    () => groupByLatencyTier([...data.bySmsType].sort((a, b) => a.avgSeconds - b.avgSeconds)),
    [data.bySmsType]
  );

  if (sortedWeekdays.length === 0) {
    return <p className='text-sm text-muted-foreground'>No click latency data available.</p>;
  }

  return (
    <div className='flex flex-col gap-5'>
      <div className='flex flex-col gap-3'>
        <div>
          <StatCard
            label='Overall'
            avgSeconds={data.overallAvgSeconds}
            clickCount={data.overallClickCount}
            highlighted
          />
        </div>
        <div className='overflow-x-auto'>
          <div className='grid min-w-210 grid-cols-7 gap-2'>
            {data.byWeekday
              .slice()
              .sort((a, b) => a.weekday - b.weekday)
              .map((wd) => (
                <StatCard
                  key={wd.weekday}
                  label={wd.weekdayName.slice(0, 3)}
                  avgSeconds={wd.avgSeconds}
                  clickCount={wd.clickCount}
                  minSeconds={weekdayRange.min}
                  maxSeconds={weekdayRange.max}
                  wowTrendPct={wd.wowTrendPct ?? null}
                />
              ))}
          </div>
        </div>
      </div>

      <div className='flex gap-6'>
        <div className='shrink-0 overflow-x-auto'>
          <div
            className='inline-grid gap-1'
            style={{ gridTemplateColumns: `auto repeat(${sortedWeekdays.length}, 1fr)` }}
          >
            <div />
            {sortedWeekdays.map(({ weekday, name }) => (
              <div key={weekday} className='px-1 text-center text-xs font-medium text-muted-foreground'>
                {name.slice(0, 3)}
              </div>
            ))}

            {HOURS.map((hour) => (
              <Fragment key={hour}>
                <div className='flex items-center pr-2 text-xs text-muted-foreground'>{formatHour(hour)}</div>
                {sortedWeekdays.map(({ weekday, name }) => {
                  const cell = gridMap.get(`${weekday}-${hour}`);
                  const avgSeconds = cell?.avgSeconds ?? null;
                  const clickCount = cell?.clickCount ?? 0;
                  const wowPct = cell?.wowTrendPct ?? null;
                  const isBest = weekday === bestWeekday && hour === bestHour;

                  return (
                    <NoddiTooltip
                      key={`${weekday}-${hour}`}
                      trigger={
                        <div
                          className={cn(
                            'size-8 rounded-md transition-colors md:h-8 md:w-12',
                            getCellColor(avgSeconds, minSeconds, maxSeconds, isBest)
                          )}
                        />
                      }
                      content={
                        <div className='flex flex-col gap-0.5'>
                          <span className='font-medium'>
                            {name} {formatHour(hour)}
                          </span>
                          {avgSeconds !== null ? (
                            <>
                              <span>Avg latency: {formatLatency(avgSeconds)}</span>
                              <span>Clicks: {clickCount}</span>
                              {wowPct !== null && (
                                <span className='flex items-center gap-1'>
                                  WoW: <WowTrend pct={wowPct} />
                                </span>
                              )}
                              {isBest && <span className='font-medium text-sky-400'>Best time slot</span>}
                            </>
                          ) : (
                            <span>No data</span>
                          )}
                        </div>
                      }
                    />
                  );
                })}
              </Fragment>
            ))}
          </div>

          <div className='mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground'>
            <span>Fast</span>
            <div className='flex gap-0.5'>
              {TRAFFIC_TIERS.map((tier) => (
                <div key={tier.bg} className={cn('size-4 rounded-xs', tier.bg)} />
              ))}
            </div>
            <span>Slow</span>
            <div className='ml-2 flex items-center gap-1'>
              <div className='size-4 rounded-xs bg-sky-500 ring-2 ring-sky-300' />
              <span>Best</span>
            </div>
            <div className='ml-2 flex items-center gap-1'>
              <div className='size-4 rounded-xs bg-muted/30' />
              <span>No data</span>
            </div>
          </div>
        </div>

        {smsTypeTiers.length > 0 && (
          <div className='flex min-w-0 flex-1 flex-col gap-4'>
            <span className='text-sm font-medium'>By SMS type</span>
            {smsTypeTiers.map((tier) => (
              <div key={tier.label} className='flex flex-col gap-2'>
                <span className='text-xs text-muted-foreground'>{tier.label}</span>
                <div className='flex flex-wrap gap-2'>
                  {tier.items.map((st) => (
                    <SmsTypeStatCard
                      key={st.smsType}
                      smsType={st.smsType}
                      avgSeconds={st.avgSeconds}
                      clickCount={st.clickCount}
                      wowTrendPct={st.wowTrendPct ?? null}
                      minSeconds={smsTypeRange.min}
                      maxSeconds={smsTypeRange.max}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({
  label,
  avgSeconds,
  clickCount,
  highlighted,
  minSeconds,
  maxSeconds,
  wowTrendPct
}: {
  label: string;
  avgSeconds: number | null;
  clickCount: number;
  highlighted?: boolean;
  minSeconds?: number;
  maxSeconds?: number;
  wowTrendPct?: number | null;
}) => {
  const hasRange = minSeconds !== undefined && maxSeconds !== undefined && avgSeconds !== null;
  const tier = hasRange ? getTrafficTier(avgSeconds, minSeconds, maxSeconds) : null;

  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 rounded-lg border px-4 py-2',
        highlighted && 'border-primary/30 bg-primary/5',
        !highlighted && tier && tier.border
      )}
    >
      <div className='flex items-center gap-1.5'>
        {tier && !highlighted && <div className={cn('size-2 shrink-0 rounded-full', tier.bg)} />}
        <span className='text-xs text-muted-foreground'>{label}</span>
      </div>
      <div className='flex items-baseline gap-2'>
        <span className='text-2xl font-semibold'>{avgSeconds !== null ? formatLatency(avgSeconds) : '-'}</span>
        <WowTrend pct={wowTrendPct ?? null} />
      </div>
      <span className='text-xs text-muted-foreground'>{clickCount} clicks</span>
    </div>
  );
};

const SmsTypeStatCard = ({
  smsType,
  avgSeconds,
  clickCount,
  wowTrendPct,
  minSeconds,
  maxSeconds
}: {
  smsType: string;
  avgSeconds: number;
  clickCount: number;
  wowTrendPct: number | null;
  minSeconds: number;
  maxSeconds: number;
}) => {
  const tier = getTrafficTier(avgSeconds, minSeconds, maxSeconds);

  return (
    <div className={cn('flex items-center gap-2 rounded-lg border px-3 py-1.5', tier.border)}>
      <div className={cn('size-2.5 shrink-0 rounded-full', tier.bg)} />
      <div className='flex flex-col'>
        <div className='flex items-center gap-1.5'>
          <span className='text-xs'>{getSmsTypeLabel(smsType)}</span>
          <WowTrend pct={wowTrendPct} />
        </div>
        <span className='text-xs text-muted-foreground'>
          {formatLatency(avgSeconds)} · {clickCount} clicks
        </span>
      </div>
    </div>
  );
};
